import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
    Plus, Sparkle, MagnifyingGlass, Check, Archive, ArrowCounterClockwise,
    Quotes, Trash, ChatCircleDots, Flask,
} from "@phosphor-icons/react";

const initialForm = { title: "", insight_text: "", emotional_theme: "", topic_tags: "" };

const STATUS_TABS = [
    { value: "suggested", label: "Suggested", description: "AI-proposed candidates awaiting review" },
    { value: "approved", label: "Approved", description: "Durable narrative artifacts you've curated" },
    { value: "archived", label: "Archived", description: "Set aside but recoverable" },
];

const CONF_PILL = {
    high: "bg-foreground text-background",
    medium: "bg-foreground/15 text-foreground",
    low: "bg-muted text-muted-foreground",
};

function ConfidencePill({ value }) {
    if (!value) return null;
    return (
        <span data-testid={`conf-pill-${value}`}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm font-mono text-[10px] tracking-[0.14em] uppercase ${CONF_PILL[value] || CONF_PILL.low}`}>
            <span className="opacity-60">conf</span>
            <span>{value}</span>
        </span>
    );
}

function EvidenceRow({ insight }) {
    const stats = [];
    if (insight.recurrence_count > 1) stats.push({ label: "recurrence", value: `×${insight.recurrence_count}` });
    if (insight.signal_count > 0) stats.push({ label: "signals", value: insight.signal_count.toLocaleString() });
    if (insight.resonance_total > 0) stats.push({ label: "resonance", value: insight.resonance_total.toLocaleString() });
    if ((insight.platforms || []).length > 0) stats.push({ label: "platforms", value: insight.platforms.join(" · ") });
    if (stats.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-[11px] font-mono text-muted-foreground">
            {stats.map((s) => (
                <span key={s.label} className="flex items-baseline gap-1.5">
                    <span className="overline text-[9px] tracking-[0.16em]">{s.label}</span>
                    <span className="text-foreground/80 tabular-nums">{s.value}</span>
                </span>
            ))}
        </div>
    );
}

function InsightCard({ insight, status, onApprove, onArchive, onRestore, onDelete, onGenerate, onPressureTest }) {
    const [expanded, setExpanded] = useState(false);
    const hasQuote = !!insight.representative_quote;

    return (
        <article data-testid={`insight-card-${insight.id}`}
                 className="border border-border bg-card hover:border-foreground/30 transition-colors p-6 group">
            <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                    <ConfidencePill value={insight.confidence} />
                    {insight.emotional_theme && (
                        <span className="overline text-[10px] text-muted-foreground">
                            {insight.emotional_theme}
                        </span>
                    )}
                </div>
                {status === "suggested" && (
                    <span data-testid="suggested-pill" className="overline text-[9px] text-accent tracking-[0.18em]">
                        candidate
                    </span>
                )}
            </div>

            <h3 className="font-display font-bold text-[19px] leading-[1.25] tracking-tight">
                {insight.title}
            </h3>

            {insight.insight_text && insight.insight_text !== insight.title && (
                <p className="text-sm text-foreground/75 mt-3 leading-relaxed">
                    {insight.insight_text}
                </p>
            )}

            <EvidenceRow insight={insight} />

            {expanded && (
                <div data-testid={`insight-evidence-${insight.id}`}
                     className="mt-5 pt-5 border-t border-border space-y-4">
                    {hasQuote && (
                        <blockquote className="relative pl-5">
                            <Quotes size={14} className="absolute left-0 top-1 text-accent" />
                            <p className="font-display italic text-foreground/90 text-[15px] leading-snug">
                                "{insight.representative_quote}"
                            </p>
                            {insight.representative_quote_platform && (
                                <p className="font-mono text-[10px] text-muted-foreground mt-1.5 tracking-wider">
                                    — {insight.representative_quote_platform}
                                </p>
                            )}
                        </blockquote>
                    )}
                    {(insight.topic_tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {insight.topic_tags.slice(0, 8).map((t) => (
                                <Badge key={t} variant="outline" className="rounded-sm text-[10px] font-mono tracking-wider">{t}</Badge>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
                <div className="flex items-center gap-1">
                    {(hasQuote || (insight.topic_tags || []).length > 0) && (
                        <Button
                            data-testid={`expand-insight-${insight.id}`}
                            variant="ghost" size="sm"
                            className="rounded-sm h-7 text-[11px] font-mono tracking-wider text-muted-foreground hover:text-foreground"
                            onClick={() => setExpanded((v) => !v)}
                        >
                            {expanded ? "Hide evidence" : "Show evidence"}
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {status === "suggested" && (
                        <>
                            <Button data-testid={`approve-insight-${insight.id}`}
                                    size="sm" className="rounded-sm h-7 text-xs"
                                    onClick={() => onApprove(insight)}>
                                <Check size={12} weight="bold" className="mr-1" /> Approve
                            </Button>
                            <Button data-testid={`archive-insight-${insight.id}`}
                                    variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0 text-muted-foreground"
                                    onClick={() => onArchive(insight)}
                                    title="Archive">
                                <Archive size={13} />
                            </Button>
                        </>
                    )}
                    {status === "approved" && (
                        <>
                            <Button data-testid={`pressure-test-insight-${insight.id}`}
                                    variant="outline" size="sm" className="rounded-sm h-7 text-xs"
                                    onClick={() => onPressureTest(insight)}
                                    title="Create a Narrative Labs hypothesis from this insight and pressure-test it against real signals">
                                <Flask size={12} weight="duotone" className="mr-1" /> Pressure-test
                            </Button>
                            <Button data-testid={`generate-from-insight-${insight.id}`}
                                    variant="ghost" size="sm" className="rounded-sm h-7 text-xs text-muted-foreground"
                                    onClick={() => onGenerate(insight)}>
                                <Sparkle size={12} className="mr-1" /> Generate
                            </Button>
                            <Button data-testid={`archive-approved-${insight.id}`}
                                    variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0 text-muted-foreground"
                                    onClick={() => onArchive(insight)}
                                    title="Archive">
                                <Archive size={13} />
                            </Button>
                        </>
                    )}
                    {status === "archived" && (
                        <>
                            <Button data-testid={`restore-insight-${insight.id}`}
                                    variant="outline" size="sm" className="rounded-sm h-7 text-xs"
                                    onClick={() => onRestore(insight)}>
                                <ArrowCounterClockwise size={12} className="mr-1" /> Restore
                            </Button>
                            <Button data-testid={`delete-insight-${insight.id}`}
                                    variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                    onClick={() => onDelete(insight)}
                                    title="Delete permanently">
                                <Trash size={13} />
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </article>
    );
}

export default function Insights() {
    const [insights, setInsights] = useState([]);
    const [counts, setCounts] = useState({ suggested: 0, approved: 0, archived: 0 });
    const [activeStatus, setActiveStatus] = useState("suggested");
    const [search, setSearch] = useState("");
    const [streams, setStreams] = useState([]);
    const [streamFilter, setStreamFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = { status: activeStatus };
            if (search) params.search = search;
            if (streamFilter) params.stream_id = streamFilter;
            const [r, c] = await Promise.all([
                api.get("/insights", { params }),
                api.get("/insights/counts", { params: streamFilter ? { stream_id: streamFilter } : {} }),
            ]);
            setInsights(r.data);
            setCounts(c.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/streams").then((r) => setStreams(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, activeStatus, streamFilter]);

    const submit = async () => {
        if (!form.title.trim() || !form.insight_text.trim()) {
            toast.error("Title and observation are required");
            return;
        }
        const tags = form.topic_tags.split(",").map((s) => s.trim()).filter(Boolean);
        await api.post("/insights", {
            title: form.title,
            insight_text: form.insight_text,
            emotional_theme: form.emotional_theme,
            topic_tags: tags,
            stream_id: streamFilter || null,
        });
        toast.success("Insight saved as Approved");
        setOpen(false);
        setForm(initialForm);
        await load();
    };

    const approve = async (insight) => {
        await api.post(`/insights/${insight.id}/approve`);
        toast.success("Promoted to Approved");
        await load();
    };
    const archive = async (insight) => {
        await api.post(`/insights/${insight.id}/archive`);
        toast.success("Archived");
        await load();
    };
    const restore = async (insight) => {
        await api.post(`/insights/${insight.id}/restore`);
        toast.success("Restored to Suggested");
        await load();
    };
    const remove = async (insight) => {
        if (!window.confirm("Delete permanently? This cannot be undone.")) return;
        await api.delete(`/insights/${insight.id}`);
        await load();
    };
    const generate = async (insight) => {
        try {
            await api.post("/content-ideas/generate-from-insight", { insight_id: insight.id, count: 6 });
            toast.success("Content ideas generated — see Content Ideas");
        } catch {
            toast.error("Generation failed");
        }
    };
    const pressureTest = async (insight) => {
        try {
            const r = await api.post(`/labs/hypotheses/from-insight/${insight.id}`);
            toast.success("Hypothesis created in Narrative Labs — opening…");
            window.location.href = `/labs/${r.data.id}`;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create hypothesis");
        }
    };

    const activeTab = STATUS_TABS.find((t) => t.value === activeStatus);

    return (
        <>
            <PageHeader
                overline="Intelligence Archive"
                title="Insights"
                description="Durable narrative artifacts. Candidates are AI-proposed; you approve the strongest. Quality over output."
                testid="insights-header"
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-insight-btn" variant="outline" size="sm" className="rounded-sm">
                                <Plus size={12} weight="bold" className="mr-1.5" /> Manual entry
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm max-w-lg" data-testid="new-insight-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display">New insight (manual)</DialogTitle>
                                <p className="text-xs text-muted-foreground mt-1">
                                    For observations not yet surfaced by AI. Saved directly as <strong>Approved</strong>.
                                </p>
                            </DialogHeader>
                            <div className="space-y-3">
                                <Input data-testid="insight-form-title" placeholder="Thesis-grade title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" />
                                <textarea data-testid="insight-form-text" rows={4} placeholder="The observation, in your own voice." value={form.insight_text} onChange={(e) => setForm({ ...form, insight_text: e.target.value })} className="w-full rounded-sm border border-input p-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary" />
                                <Input data-testid="insight-form-theme" placeholder="Emotional theme (optional)" value={form.emotional_theme} onChange={(e) => setForm({ ...form, emotional_theme: e.target.value })} className="rounded-sm" />
                                <Input data-testid="insight-form-tags" placeholder="topic tags, comma separated" value={form.topic_tags} onChange={(e) => setForm({ ...form, topic_tags: e.target.value })} className="rounded-sm" />
                            </div>
                            <DialogFooter>
                                <Button data-testid="confirm-new-insight" onClick={submit} className="rounded-sm">Save as Approved</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />

            <PageBody>
                {/* Pipeline tabs — Suggested · Approved · Archived */}
                <div data-testid="insights-pipeline-tabs"
                     className="border-b border-border flex items-end gap-0 mb-7 overflow-x-auto">
                    {STATUS_TABS.map((tab) => {
                        const active = tab.value === activeStatus;
                        return (
                            <button
                                key={tab.value}
                                type="button"
                                data-testid={`insights-tab-${tab.value}`}
                                onClick={() => setActiveStatus(tab.value)}
                                className={`group px-5 py-3 border-b-2 transition-colors text-left ${
                                    active
                                        ? "border-foreground text-foreground"
                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                <div className="flex items-baseline gap-2.5">
                                    <span className="font-display font-semibold text-base">{tab.label}</span>
                                    <span data-testid={`count-${tab.value}`}
                                          className={`font-mono tabular-nums text-xs ${active ? "text-foreground" : "text-muted-foreground/70"}`}>
                                        {(counts[tab.value] || 0).toString().padStart(2, "0")}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                    <div className="flex-1 border-b-2 border-transparent" />
                </div>

                {/* Tab description + filters */}
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <p data-testid="active-tab-description" className="font-mono text-[11px] text-muted-foreground tracking-wider">
                        {activeTab?.description}
                    </p>
                    <div className="flex gap-2 items-center flex-wrap">
                        <select
                            data-testid="insights-stream-filter"
                            value={streamFilter}
                            onChange={(e) => setStreamFilter(e.target.value)}
                            className="text-[11px] font-mono tracking-wider px-3 py-1.5 rounded-sm border border-border bg-background hover:border-foreground/40 transition-colors focus:outline-none focus:ring-1 focus:ring-foreground/40"
                        >
                            <option value="">All streams</option>
                            {streams.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                        <div className="relative">
                            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input data-testid="insights-search" placeholder="Search…" className="rounded-sm pl-7 h-8 text-xs w-44"
                                   value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : insights.length === 0 ? (
                    <EmptyState
                        title={
                            activeStatus === "suggested" ? "No candidates waiting."
                            : activeStatus === "approved" ? "Nothing approved yet."
                            : "Archive is empty."
                        }
                        description={
                            activeStatus === "suggested"
                                ? "Run an analysis from a stream — the AI will propose candidate insights here for your review."
                                : activeStatus === "approved"
                                ? "Promote candidates from the Suggested tab, or add a manual entry."
                                : "Insights you set aside will live here, recoverable any time."
                        }
                        action={activeStatus === "suggested" ? null : (
                            <Button onClick={() => setActiveStatus("suggested")} variant="outline"
                                    data-testid="empty-cta-go-suggested" className="rounded-sm">
                                <ChatCircleDots size={13} className="mr-1.5" /> Review suggested
                            </Button>
                        )}
                        testid="insights-empty"
                    />
                ) : (
                    <div data-testid="insights-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {insights.map((i) => (
                            <InsightCard
                                key={i.id}
                                insight={i}
                                status={activeStatus}
                                onApprove={approve}
                                onArchive={archive}
                                onRestore={restore}
                                onDelete={remove}
                                onGenerate={generate}
                                onPressureTest={pressureTest}
                            />
                        ))}
                    </div>
                )}
            </PageBody>
        </>
    );
}
